// src/pages/admin/AdminOrganizations.js
// Superadmin: every organization by status, with search, paging and actions. A new
// sign-up's organization waits here ('pending') until approved.
// ?status= picks the tab and ?org=<id> opens that organization's details.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminAPI } from '../../utils/api';
import { useStore } from '../../store';
import { ACTION_LOG_LABEL, STATUS_LABEL, errorOf, fmtDay, fmtDate } from './common';

const TABS = [
  { id: 'pending', label: 'Pending approval' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'deactivated', label: 'Deactivated' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];
const LIMIT = 20;

// What the superadmin can do to an organization, by action id (= API path segment).
// reason: null (none) | 'optional' | 'required'
const ORG_ACTIONS = {
  approve: {
    label: 'Approve', btn: 'btn-primary', reason: null,
    describe: (o) => (o.owner_email_verified_at
      ? `${o.name} becomes active. ${o.owner_name} can sign in, invite their team, and gets an email saying so.`
      : `${o.name} becomes active, but ${o.owner_name} hasn't verified their email yet, so they can't sign in until they do. They'll get the approval email now.`),
  },
  reject: {
    label: 'Reject', btn: 'btn-danger', reason: 'optional',
    describe: (o) => `No one in ${o.name} can sign in. ${o.owner_name} gets an email, including the reason if you give one. You can still approve it later.`,
  },
  suspend: {
    label: 'Suspend', btn: 'btn-danger', reason: 'required',
    describe: (o) => `All ${o.member_count} member(s) of ${o.name} are signed out and can't sign in, and its scheduled posts are paused until you reactivate it. Every member gets an email with the reason.`,
  },
  deactivate: {
    label: 'Deactivate', btn: 'btn-outline', reason: 'optional',
    describe: (o) => `All ${o.member_count} member(s) of ${o.name} are signed out and can't sign in, and its scheduled posts are paused. Its data is kept, and you can reactivate it at any time.`,
  },
  reactivate: {
    label: 'Reactivate', btn: 'btn-primary', reason: null,
    describe: (o) => `Members of ${o.name} can sign in again and each gets an email. Paused scheduled posts resume — any whose time has already passed will publish straight away.`,
  },
  delete: {
    label: 'Delete', btn: 'btn-danger', reason: null,
    describe: (o) => `Permanently deletes ${o.name}: all ${o.member_count} member sign-in(s), posts, connected social accounts, groups and media. This can't be undone.`,
  },
};

function availableActions(o) {
  const byStatus = {
    pending: ['approve', 'reject'],
    active: ['suspend', 'deactivate'],
    suspended: ['reactivate', 'deactivate'],
    deactivated: ['reactivate'],
    rejected: ['approve'],
  }[o.status] || [];
  return [...byStatus, 'delete'];
}

function OrgStatusBadge({ org }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      <span className={`badge badge-${org.status}`}>{STATUS_LABEL[org.status] || org.status}</span>
      {org.status === 'pending' && !org.owner_email_verified_at && <span className="badge badge-unverified">Email unverified</span>}
    </span>
  );
}

// Confirms one action (asking for a reason or the typed name where needed) and runs it.
function OrgActionDialog({ org, action, onClose, onDone }) {
  const cfg = ORG_ACTIONS[action];
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const fetchAdminPending = useStore(s => s.fetchAdminPending);

  const canSubmit = !busy
    && (cfg.reason !== 'required' || reason.trim())
    && (action !== 'delete' || confirmName.trim() === org.name.trim());

  const submit = async () => {
    setBusy(true);
    try {
      if (action === 'delete') await adminAPI.deleteOrg(org.id, confirmName.trim());
      else await adminAPI.actOrg(org.id, action, cfg.reason ? { reason: reason.trim() || null } : undefined);
      toast.success(action === 'delete' ? `${org.name} was deleted` : `${cfg.label}: done for ${org.name}`);
      fetchAdminPending();
      onDone(action);
    } catch (err) {
      toast.error(errorOf(err, `Could not ${cfg.label.toLowerCase()}`));
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={e => { e.stopPropagation(); if (!busy) onClose(); }}>
      <div className="modal" style={{ width: 440, maxWidth: 'calc(100vw - 32px)' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">{cfg.label} — {org.name}</div></div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: '#4a5568', lineHeight: 1.6, marginBottom: 14 }}>{cfg.describe(org)}</p>
          {cfg.reason && (
            <div className="form-group">
              <label className="form-label">Reason {cfg.reason === 'required' ? <span className="req">*</span> : <span style={{ fontWeight: 400, color: '#8892a4' }}>(optional — included in the email)</span>}</label>
              <textarea className="form-textarea" rows={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
            </div>
          )}
          {action === 'delete' && (
            <div className="form-group">
              <label className="form-label">Type <strong>{org.name}</strong> to confirm</label>
              <input className="form-input" value={confirmName} onChange={e => setConfirmName(e.target.value)} autoFocus />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" disabled={busy} onClick={onClose}>Cancel</button>
          <button className={`btn ${cfg.btn === 'btn-outline' ? 'btn-primary' : cfg.btn}`} disabled={!canSubmit} onClick={submit}>
            {busy ? 'Working…' : cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#8892a4', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#1a1f2e' }}>{children}</div>
    </div>
  );
}

// Full details of one organization: members, usage, history and every available action.
function OrgDetailModal({ orgId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null);

  const load = () => adminAPI.organization(orgId)
    .then(r => setData(r.data))
    .catch(err => { toast.error(errorOf(err, 'Could not load this organization')); onClose(); });

  useEffect(() => { load(); }, [orgId]);

  const done = (a) => {
    setAction(null);
    onChanged();
    if (a === 'delete') onClose();
    else load();
  };

  const o = data?.organization;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 640, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{o ? o.name : 'Organization'}</div>
          <span style={{ cursor: 'pointer', color: '#8892a4', fontSize: 18 }} onClick={onClose}>×</span>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!data ? <div className="loading-center" style={{ padding: 30 }}><div className="spinner" /></div> : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <OrgStatusBadge org={o} />
                <span style={{ fontSize: 12, color: '#4a5568' }}>Owner: <strong>{o.owner_name}</strong> · {o.owner_email}</span>
              </div>
              {o.status_reason && (
                <div style={{ padding: '8px 12px', background: '#f8f9fb', border: '1px solid #e8eaf0', borderRadius: 6, fontSize: 12, marginBottom: 16 }}>
                  <strong>Reason:</strong> {o.status_reason}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 18 }}>
                <Field label="Registered">{fmtDate(o.created_at)}</Field>
                <Field label="Approved">{fmtDate(o.approved_at)}</Field>
                <Field label="Status changed">{fmtDate(o.status_changed_at)}</Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8, marginBottom: 18 }}>
                {[['Members', data.members.length], ['Social accounts', data.counts.accounts], ['Posts', data.counts.posts],
                  ['Scheduled', data.counts.queued_posts], ['Groups', data.counts.groups], ['Media files', data.counts.media_files],
                  ['Open invitations', data.counts.open_invitations]].map(([label, n]) => (
                  <div key={label} className="card" style={{ padding: '8px 10px' }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{n}</div>
                    <div style={{ fontSize: 10, color: '#8892a4' }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
                {availableActions(o).map(a => (
                  <button key={a} className={`btn ${ORG_ACTIONS[a].btn}`} onClick={() => setAction(a)}>{ORG_ACTIONS[a].label}</button>
                ))}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Members</div>
              <table className="data-table" style={{ marginBottom: 18 }}>
                <tbody>
                  {data.members.map(m => (
                    <tr key={m.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users?status=all&user=${m.id}`)}>
                      <td style={{ width: 'auto' }}>
                        <div style={{ fontWeight: 600, color: '#0f766e' }}>{m.name}</div>
                        <div style={{ color: '#8892a4', fontSize: 11 }}>{m.email}</div>
                      </td>
                      <td style={{ textTransform: 'capitalize' }}>{m.org_role}</td>
                      <td>
                        {m.status !== 'active' && <span className={`badge badge-${m.status}`}>{STATUS_LABEL[m.status] || m.status}</span>}
                        {!m.email_verified_at && <span className="badge badge-unverified">Email unverified</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', color: '#8892a4', textAlign: 'right' }}>{m.last_login_at ? `Signed in ${fmtDay(m.last_login_at)}` : 'Never signed in'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>History</div>
              {data.history.length === 0 ? <div style={{ fontSize: 12, color: '#8892a4' }}>No admin actions yet.</div> : (
                <table className="data-table">
                  <tbody>
                    {data.history.map(h => (
                      <tr key={h.id}>
                        <td style={{ width: 'auto' }}>
                          <div style={{ fontWeight: 600 }}>{ACTION_LOG_LABEL[h.action] || h.action}</div>
                          {h.reason && <div style={{ color: '#4a5568', marginTop: 2 }}>{h.reason}</div>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', color: '#8892a4', textAlign: 'right' }}>{fmtDate(h.created_at)}{h.admin_name ? ` · ${h.admin_name}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
      {action && o && <OrgActionDialog org={o} action={action} onClose={() => setAction(null)} onDone={done} />}
    </div>
  );
}

export default function AdminOrganizations() {
  const [params, setParams] = useSearchParams();
  const status = TABS.some(t => t.id === params.get('status')) ? params.get('status') : 'pending';
  const openOrgId = params.get('org');
  const adminPending = useStore(s => s.adminPending);

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('created_at:desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState(null); // { org, action } from a row button
  const [reloadKey, setReloadKey] = useState(0);

  // Debounce typing into the search box
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const [sortBy, order] = sort.split(':');
    let cancelled = false;
    setData(d => (d ? { ...d, loading: true } : d));
    adminAPI.organizations({ status, q: q || undefined, sort: sortBy, order, page, limit: LIMIT })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(err => toast.error(errorOf(err, 'Could not load organizations')));
    return () => { cancelled = true; };
  }, [status, q, sort, page, reloadKey]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: key === 'org' });
  };
  const changeTab = (id) => { setPage(1); setParam('status', id); };
  const reload = () => setReloadKey(k => k + 1);

  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const first = data?.total ? (page - 1) * LIMIT + 1 : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-header">
        <div>
          <div className="page-title">Organizations</div>
          <div className="page-subtitle">Approve new organizations and manage which ones can use Post Calendar</div>
        </div>
      </div>
      <div style={{ display: 'flex', padding: '0 16px', background: '#fff', borderBottom: '1px solid #e8eaf0', flexShrink: 0, overflowX: 'auto' }}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => changeTab(t.id)}
            style={{ padding: '11px 14px', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', color: status === t.id ? '#f4a118' : '#8892a4',
              borderBottom: `2px solid ${status === t.id ? '#f4a118' : 'transparent'}`, fontWeight: status === t.id ? 600 : 500 }}>
            {t.label}
            {t.id === 'pending' && adminPending > 0 && <span className="badge" style={{ marginLeft: 6, background: '#f4a118', color: '#fff' }}>{adminPending}</span>}
          </div>
        ))}
      </div>
      <div className="toolbar">
        <div className="search-box">
          <span style={{ color: '#aaa' }}>🔍</span>
          <input placeholder="Search organization, member name or email" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 260 }} />
        </div>
        <select className="form-select" style={{ width: 'auto', padding: '6px 8px', fontSize: 12 }} value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="name:asc">Name A–Z</option>
          <option value="member_count:desc">Most members</option>
        </select>
        <span className="count-text ml-auto">{data ? `${first}–${first ? first + data.organizations.length - 1 : 0} of ${data.total}` : ''}</span>
        <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
        <button className="btn btn-outline" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!data ? <div className="loading-center"><div className="spinner" /></div> :
          data.organizations.length === 0 ? (
            <div className="empty-state">
              <span style={{ fontSize: 40 }}>🏢</span>
              <h3>No organizations here</h3>
              <p>{q ? 'Nothing matches your search.' : status === 'pending' ? 'No organizations are waiting for approval.' : 'No organizations with this status.'}</p>
            </div>
          ) : (
            <table className="data-table" style={{ opacity: data.loading ? 0.6 : 1, minWidth: 820 }}>
              <thead>
                <tr><th style={{ width: 'auto' }}>Organization</th><th>Owner</th><th>Status</th><th>Registered</th><th>Members</th><th>Accounts / Posts</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>
                {data.organizations.map(o => (
                  <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => setParam('org', o.id)}>
                    <td style={{ width: 'auto', fontWeight: 600 }}>{o.name}</td>
                    <td>
                      <div>{o.owner_name}</div>
                      <div style={{ color: '#8892a4', fontSize: 11 }}>{o.owner_email}</div>
                    </td>
                    <td><OrgStatusBadge org={o} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDay(o.created_at)}</td>
                    <td>{o.member_count}</td>
                    <td>{o.account_count} / {o.post_count}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {o.status === 'pending' && <>
                        <button className="btn btn-primary" style={{ padding: '4px 10px' }} onClick={() => setQuick({ org: o, action: 'approve' })}>Approve</button>{' '}
                        <button className="btn btn-danger" style={{ padding: '4px 10px' }} onClick={() => setQuick({ org: o, action: 'reject' })}>Reject</button>{' '}
                      </>}
                      <button className="btn btn-outline" style={{ padding: '4px 10px' }} onClick={() => setParam('org', o.id)}>Manage</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
      {openOrgId && <OrgDetailModal orgId={openOrgId} onClose={() => setParam('org', null)} onChanged={reload} />}
      {quick && (
        <OrgActionDialog org={quick.org} action={quick.action} onClose={() => setQuick(null)} onDone={() => { setQuick(null); reload(); }} />
      )}
    </div>
  );
}
